import type { ApiEndpointBuilder } from "../apiEndpointHelpers";

/** The manual health a staff member can set (Record pages row 27). */
export type AdminV2CrmContactHealthOverride = "cold" | "warm" | "hot";

/** One selected contact's outcome, keyed by the id as it was sent. */
export interface AdminV2CrmPeopleBulkHealthResult {
  personId: string;
  /** A contact with a linked user keeps the health their product use gives them. */
  status: "updated" | "skipped_linked_account";
}

/** `POST /admin/v2/crm/people/bulk-actions/health` success body. */
export interface AdminV2CrmPeopleBulkHealthResponse {
  results: AdminV2CrmPeopleBulkHealthResult[];
  updatedCount: number;
  skippedCount: number;
}

export interface AdminV2CrmPeopleBulkHealthRequest {
  /** 1–500 contact ids; the server de-duplicates them. */
  personIds: string[];
  contactHealthOverride: AdminV2CrmContactHealthOverride;
  /** Required; 500 characters or fewer after trimming. */
  contactHealthOverrideReason: string;
}

/**
 * True only when the server itself refused the request: a 4xx it sent, or
 * its own 500 for a rolled-back write. The endpoint is all or nothing, so
 * these changed nothing. A timeout, dropped connection, client-side cancel
 * (the base query's 499) or gateway error may have followed a committed
 * write, so the outcome is unknown.
 */
export const isDefiniteBulkHealthRefusal = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;
  const { status, data } = error as { status?: unknown; data?: unknown };
  if (typeof status !== "number") return false;
  if (status === 408 || status === 499) return false;
  if (status >= 400 && status < 500) return true;
  return (
    status === 500 &&
    typeof data === "object" &&
    data !== null &&
    (data as { errorCode?: unknown }).errorCode ===
      "crm_contact_health_bulk_update_failed"
  );
};

/** Reload after a success or an unknown outcome; a refusal changed nothing. */
export const getBulkHealthInvalidationTags = (
  result: AdminV2CrmPeopleBulkHealthResponse | undefined,
  error: unknown,
  personIds: string[],
) =>
  result !== undefined ||
  (error !== undefined && !isDefiniteBulkHealthRefusal(error))
    ? [
        { type: "AdminV2CrmPeople" as const, id: "LIST" },
        ...personIds.map((personId) => ({
          type: "AdminV2CrmPeople" as const,
          id: personId,
        })),
        { type: "AdminV2CrmTimeline" as const, id: "LIST" },
        { type: "AdminV2AuditLog" as const, id: "LIST" },
      ]
    : [];

/**
 * Contacts bulk bar "Update health — N selected" (Contacts delta row 104).
 *
 * @remarks
 * All or nothing on the server: every contact is checked first, then all are
 * updated in one transaction. Each update writes an audit entry and a health
 * Activity row, so the list, each record, Activity and the audit log refetch.
 */
export const buildAdminV2CrmPeopleBulkHealthEndpoints = (
  builder: ApiEndpointBuilder,
) => ({
  bulkUpdateAdminV2CrmPeopleHealth: builder.mutation<
    AdminV2CrmPeopleBulkHealthResponse,
    AdminV2CrmPeopleBulkHealthRequest
  >({
    query: (body) => ({
      url: "/admin/v2/crm/people/bulk-actions/health",
      method: "POST",
      data: body,
    }),
    invalidatesTags: (result, error, { personIds }) =>
      getBulkHealthInvalidationTags(result, error, personIds),
  }),
});
